import { Box, Text, VStack, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type {
    CccOrderingRule,
    OrderingSettings,
    SettingsResponse,
} from '../../../../hooks/useGetSettings';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import {
    useRelatedInsurance,
    type InsuranceSearchResult,
} from '../../../../hooks/useSearchInsurance';
import {
    SettingsCheckTable,
    SettingsFlow,
    SettingsFlowSplit,
    useSettingsDetailCrumbs,
} from '../../../../components/settings/primitives';
import CarrierSearchField from '../../../../components/settings/CarrierSearchField';
import {
    toCccOrderingRules,
    toStoredCccOrdering,
} from '../../../../components/settings/cccOrderingValue';
import OemMakeSearchField from '../oem/OemMakeSearchField';
import { Button, Notice } from '../../../../ui';

/** Sentinel ids for the "All …" row pinned above each pick list. */
const ALL_MAKES_ROW = '__all_makes__';
const ALL_CARRIERS_ROW = '__all_carriers__';

type MakeOption = SettingsResponse['oemOptions'][number];

interface NamedRecord {
    _id: string;
    name: string;
}

interface CccRuleDraft {
    /** Every make, stored as the 'all' sentinel so makes added later count too. */
    isAllMakes: boolean;
    /** Makes offered as rows: the ones searched up, plus their OEM's other makes. */
    makeRowIds: Array<string>;
    makeIds: Array<string>;
    isAllCarriers: boolean;
    /** Carriers offered as rows: the ones searched up, plus their group mates. */
    carrierRows: Array<NamedRecord>;
    carrierIds: Array<string>;
}

const unique = (ids: Array<string>): Array<string> => Array.from(new Set(ids));

/** Adds records that are not listed yet, keeping the order they arrived in. */
const mergeNamed = (
    current: Array<NamedRecord>,
    incoming: Array<NamedRecord>,
): Array<NamedRecord> => {
    const known = new Set(current.map((entry) => entry._id));
    return [...current, ...incoming.filter((entry) => !known.has(entry._id))];
};

export interface CccOrderingRuleDetailSectionProps {
    /** The rule's index in the cccOrdering list, or 'new' for the add case. */
    ruleId: string;
    ordering: OrderingSettings;
    oemOptions: SettingsResponse['oemOptions'];
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

/**
 * Add or edit one custom CCC ordering rule, full screen: the makes it covers on
 * the left, the insurance carriers on the right. One step, the same split the
 * vendor, carrier and OEM screens use, so adding and editing are one screen.
 *
 * Both halves are searched rather than listed, and each pick brings its group
 * with it — an OEM's other makes on the left, an insurer's other companies on
 * the right — because a rule is nearly always meant for the whole group. Every
 * mate arrives checked and can be unchecked.
 *
 * Saving writes the whole ordering subdocument through POST /settings/update,
 * which is how this endpoint works, so the rule is merged into the current list
 * here rather than staged on the section's draft.
 */
const CccOrderingRuleDetailSection = ({
    ruleId,
    ordering,
    oemOptions,
    refetch,
}: CccOrderingRuleDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();

    const rules = useMemo(() => toCccOrderingRules(ordering.cccOrdering), [ordering.cccOrdering]);
    const isAddMode = ruleId === 'new';
    const ruleIndex = isAddMode ? -1 : Number(ruleId);
    const rule = !isAddMode && Number.isInteger(ruleIndex) ? rules[ruleIndex] : undefined;

    const makeOptions = useMemo<Array<MakeOption>>(
        () =>
            oemOptions
                .filter((option) => option.name && option.name.trim() !== '')
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name)),
        [oemOptions],
    );

    /**
     * Names for every make id that can end up on a row: the options themselves,
     * the related makes hanging off them, and whatever the saved rule names, so
     * a make the options no longer carry still reads as itself.
     */
    const makeNameById = useMemo(() => {
        const names = new Map<string, string>();
        oemOptions.forEach((option) => {
            names.set(option._id, option.name);
            option.similarMakes?.forEach((similar) => names.set(similar._id, similar.name));
        });
        if (rule && rule.makes !== 'all') {
            rule.makes.forEach((make) => {
                if (make.name) names.set(make._id, make.name);
            });
        }
        return names;
    }, [oemOptions, rule]);

    const [draft, setDraft] = useState<CccRuleDraft>(() => {
        const savedMakeIds = rule && rule.makes !== 'all' ? rule.makes.map((make) => make._id) : [];
        // A saved rule's makes come back with their OEM's other makes listed
        // beside them, so the group is visible while editing.
        const groupMateIds = oemOptions
            .filter((option) => savedMakeIds.includes(option._id))
            .flatMap((option) => option.similarMakes?.map((similar) => similar._id) ?? []);
        const savedCarriers =
            rule && rule.carriers !== 'all'
                ? rule.carriers.map((carrier) => ({ _id: carrier._id, name: carrier.name }))
                : [];
        return {
            // A new rule starts with nothing on either side: all makes for all
            // carriers is what the Always option already says, so it would be a
            // rule that means nothing.
            isAllMakes: !!rule && rule.makes === 'all',
            makeRowIds: unique([...savedMakeIds, ...groupMateIds]),
            makeIds: savedMakeIds,
            isAllCarriers: !!rule && rule.carriers === 'all',
            carrierRows: savedCarriers,
            carrierIds: savedCarriers.map((carrier) => carrier._id),
        };
    });

    // Groups of picked carriers whose companies have not been listed yet,
    // looked up one at a time in pick order. A queue rather than a single slot:
    // picking a second carrier while the first group is loading must not drop
    // the first group. Each group is folded in once: re-picking a name from a
    // group the user has already pruned must not tick its companies again.
    const [pendingGroupIds, setPendingGroupIds] = useState<Array<string>>([]);
    const mergedGroupIds = useRef<Set<string>>(new Set());
    const activeGroupId = pendingGroupIds[0];
    const {
        data: relatedData,
        isSuccess: isRelatedSuccess,
        isError: isRelatedError,
    } = useRelatedInsurance(activeGroupId);
    // Carriers whose group did not load, so the screen can say which picks
    // were not expanded instead of the queue stalling on them.
    const [failedGroupCarriers, setFailedGroupCarriers] = useState<Array<string>>([]);
    const carrierNameByGroup = useRef<Map<string, string>>(new Map());

    useEffect(() => {
        if (!activeGroupId) return;
        if (!isRelatedSuccess && !isRelatedError) return;
        // Every outcome advances the queue: a failed or empty lookup must not
        // hold up the groups picked after it.
        setPendingGroupIds((prev) => prev.filter((groupId) => groupId !== activeGroupId));
        if (isRelatedError) {
            const carrierName = carrierNameByGroup.current.get(activeGroupId);
            if (carrierName) {
                setFailedGroupCarriers((prev) => unique([...prev, carrierName]));
            }
            return;
        }
        if (mergedGroupIds.current.has(activeGroupId)) return;
        mergedGroupIds.current.add(activeGroupId);
        const related = (relatedData?.relatedCompanies ?? []).map((company) => ({
            _id: company._id,
            name: company.name,
        }));
        if (related.length === 0) return;
        setDraft((prev) => ({
            ...prev,
            carrierRows: mergeNamed(prev.carrierRows, related),
            carrierIds: unique([...prev.carrierIds, ...related.map((company) => company._id)]),
        }));
    }, [activeGroupId, isRelatedSuccess, isRelatedError, relatedData]);

    // An out-of-range index has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !rule) navigate('/settings/ordering', { replace: true });
    }, [isAddMode, rule, navigate]);

    useSettingsDetailCrumbs([
        { label: isAddMode ? 'Add CCC ordering rule' : 'CCC ordering rule' },
    ]);

    const closeFlow = () => navigate('/settings/ordering');

    const makeRows = useMemo(
        () =>
            draft.makeRowIds
                .map((id) => ({ id, label: makeNameById.get(id) ?? 'Unknown make' }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [draft.makeRowIds, makeNameById],
    );

    const carrierRows = useMemo(
        () =>
            draft.carrierRows
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name)),
        [draft.carrierRows],
    );

    const hasMakes = draft.isAllMakes || draft.makeIds.length > 0;
    const hasCarriers = draft.isAllCarriers || draft.carrierIds.length > 0;
    // A carrier's group mates join the draft when its lookup returns, so saving
    // before then would store the carrier without them.
    const isLoadingCarrierGroups = pendingGroupIds.length > 0;
    const canSave = hasMakes && hasCarriers && !isLoadingCarrierGroups;

    const handleSelectMake = (option: MakeOption) => {
        const group = [option, ...(option.similarMakes ?? [])].map((make) => make._id);
        setDraft((prev) => ({
            ...prev,
            // Naming a make is the opposite of covering every one of them.
            isAllMakes: false,
            makeRowIds: unique([...prev.makeRowIds, ...group]),
            makeIds: unique([...prev.makeIds, ...group]),
        }));
    };

    const handleSelectCarrier = (company: InsuranceSearchResult) => {
        setDraft((prev) => ({
            ...prev,
            isAllCarriers: false,
            carrierRows: mergeNamed(prev.carrierRows, [{ _id: company._id, name: company.name }]),
            carrierIds: unique([...prev.carrierIds, company._id]),
        }));
        const groupId = company.groupId;
        if (groupId && !mergedGroupIds.current.has(groupId)) {
            carrierNameByGroup.current.set(groupId, company.name);
            setPendingGroupIds((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
        }
    };

    /**
     * "All OEMs" is the stored 'all' sentinel rather than every box ticked, so
     * it clears the named makes instead of listing them — a rule on every make
     * has nothing to prune.
     */
    const handleToggleMake = (id: string, checked: boolean) => {
        if (id === ALL_MAKES_ROW) {
            setDraft((prev) => ({
                ...prev,
                isAllMakes: checked,
                makeIds: checked ? [] : prev.makeIds,
            }));
            return;
        }
        setDraft((prev) => ({
            ...prev,
            isAllMakes: false,
            makeIds: checked
                ? unique([...prev.makeIds, id])
                : prev.makeIds.filter((makeId) => makeId !== id),
        }));
    };

    const handleToggleCarrier = (id: string, checked: boolean) => {
        if (id === ALL_CARRIERS_ROW) {
            setDraft((prev) => ({
                ...prev,
                isAllCarriers: checked,
                carrierIds: checked ? [] : prev.carrierIds,
            }));
            return;
        }
        setDraft((prev) => ({
            ...prev,
            isAllCarriers: false,
            carrierIds: checked
                ? unique([...prev.carrierIds, id])
                : prev.carrierIds.filter((carrierId) => carrierId !== id),
        }));
    };

    const writeRules = (
        nextRules: Array<CccOrderingRule>,
        successTitle: string,
        successDescription: string,
        errorTitle: string,
    ) => {
        updateSettings.mutate(
            { ordering: { ...ordering, cccOrdering: toStoredCccOrdering(nextRules) } },
            {
                onSuccess: () => {
                    toast({
                        title: successTitle,
                        description: successDescription,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: errorTitle,
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleSave = () => {
        if (!canSave) return;

        const nextRule: CccOrderingRule = {
            makes: draft.isAllMakes
                ? 'all'
                : makeRows
                      .filter((row) => draft.makeIds.includes(row.id))
                      .map((row) => ({ _id: row.id, name: row.label })),
            carriers: draft.isAllCarriers
                ? 'all'
                : carrierRows.filter((carrier) => draft.carrierIds.includes(carrier._id)),
        };

        const nextRules = rule
            ? rules.map((candidate, index) => (index === ruleIndex ? nextRule : candidate))
            : [...rules, nextRule];

        writeRules(
            nextRules,
            isAddMode ? 'CCC ordering rule added' : 'CCC ordering rule updated',
            'PartBay will order through CCC for the makes and carriers this rule names.',
            isAddMode ? 'Could not add rule' : 'Could not update rule',
        );
    };

    const handleRemove = () => {
        if (!rule) return;
        writeRules(
            rules.filter((_, index) => index !== ruleIndex),
            'CCC ordering rule removed',
            'The rule has been removed.',
            'Could not remove rule',
        );
    };

    if (!isAddMode && !rule) return null;

    const left = (
        // A flex column, not a scrolling box: the makes table takes whatever
        // height is left over and scrolls on its own.
        <VStack align="stretch" spacing={6} flex={1} minH={0} px={6} pb={6} overflow="hidden">
            <Notice tone="info">
                A job is ordered through CCC when its make and its carrier are both covered by one
                rule.
            </Notice>

            <Box flex={1} minH={0} display="flex" flexDirection="column">
                <Text textStyle="labelSm" color="fg.default" mb={1} flexShrink={0}>
                    OEMs
                </Text>
                <Text textStyle="bodySm" color="fg.muted" mb={2.5} flexShrink={0}>
                    Search the makes PartBay knows. The rest of that make's OEM comes with it —
                    uncheck any this rule should leave out.
                </Text>
                {/* Searching stays offered while All OEMs is on: naming a make
                    is how the user narrows off it, so the field is the way out
                    rather than something to unlock first. */}
                <Box mb={3} flexShrink={0}>
                    <OemMakeSearchField
                        options={makeOptions}
                        addedMakeIds={draft.makeIds}
                        onSelect={handleSelectMake}
                    />
                </Box>
                <SettingsCheckTable
                    fill
                    columnLabel="MAKE"
                    checkLabel="Include"
                    emptyMessage="Search for a make to add it to this rule"
                    rows={[
                        {
                            id: ALL_MAKES_ROW,
                            label: 'All OEMs',
                            isChecked: draft.isAllMakes,
                            isEmphasis: true,
                        },
                        ...makeRows.map((row) => ({
                            id: row.id,
                            label: row.label,
                            isChecked: draft.isAllMakes || draft.makeIds.includes(row.id),
                            isDisabled: draft.isAllMakes,
                        })),
                    ]}
                    onToggle={handleToggleMake}
                />
            </Box>
        </VStack>
    );

    return (
        <SettingsFlow
            steps={[{ id: 'rule', label: 'CCC ordering rule' }]}
            activeStepId="rule"
            onCancel={closeFlow}
            onSave={handleSave}
            saveLabel={isAddMode ? 'Add Rule' : 'Save Rule'}
            isSaveDisabled={!canSave}
            isSaving={updateSettings.isPending}
            actions={
                rule ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemove}
                        isDisabled={updateSettings.isPending}
                    >
                        Remove Rule
                    </Button>
                ) : undefined
            }
        >
            <SettingsFlowSplit
                leftWidth="50%"
                leftTitle={isAddMode ? 'Add rule' : 'Rule'}
                left={left}
                isLeftFlush
                rightTitle="Insurance companies"
                rightDescription="Which carriers this rule covers. Others in the same group come with the one you pick."
                right={
                    <Box flex={1} minH={0} px={6} pb={6} display="flex" flexDirection="column">
                        <Box mb={3} flexShrink={0}>
                            <CarrierSearchField
                                onSelect={handleSelectCarrier}
                                // Carriers already on the rule are the ones
                                // spoken for here, not the org's DRP list.
                                statusFor={(company) =>
                                    draft.carrierIds.includes(company._id)
                                        ? 'Already added'
                                        : undefined
                                }
                            />
                        </Box>
                        {isLoadingCarrierGroups && (
                            <Text textStyle="bodySm" color="fg.muted" mb={3} flexShrink={0}>
                                Adding the rest of this carrier's group…
                            </Text>
                        )}
                        {failedGroupCarriers.length > 0 && (
                            <Box mb={3} flexShrink={0}>
                                <Notice tone="warning">
                                    Could not load the group for {failedGroupCarriers.join(', ')}. Only
                                    the carrier you picked was added — search for any others it should
                                    include.
                                </Notice>
                            </Box>
                        )}
                        <SettingsCheckTable
                            fill
                            columnLabel="INSURANCE"
                            checkLabel="Applies"
                            emptyMessage="Search for a carrier to add it to this rule"
                            rows={[
                                {
                                    id: ALL_CARRIERS_ROW,
                                    label: 'All insurance',
                                    isChecked: draft.isAllCarriers,
                                    isEmphasis: true,
                                },
                                ...carrierRows.map((carrier) => ({
                                    id: carrier._id,
                                    label: carrier.name,
                                    isChecked:
                                        draft.isAllCarriers || draft.carrierIds.includes(carrier._id),
                                    isDisabled: draft.isAllCarriers,
                                })),
                            ]}
                            onToggle={handleToggleCarrier}
                        />
                    </Box>
                }
                isRightFlush
            />
        </SettingsFlow>
    );
};

export default CccOrderingRuleDetailSection;
